import { DraftData, ThemeJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { useState } from 'react';
import { AddAttributeFunction } from './state.mapping';
import { StationeryProduct } from '@shared/utils/print-price/types';

export type CardGeneralData = DraftData['config']['customizations'];

export type ProductAttributeState = {
  productAttributeId: string;
  getOptionId: (draftData: ThemeJson) => string;
  setOptionId: (newState: string) => void;
  allowedOptionIDs: string[];
};

type StateReducerAction =
  | {
      task: 'registerAttribute';
      newAttribute: ProductAttributeState;
    }
  | {
      task: 'removeAttribute';
      attributeId: string;
    };

const stateReducer = (state: ProductAttributeState[], action: StateReducerAction): ProductAttributeState[] => {
  switch (action.task) {
    case 'registerAttribute': {
      const replaceStep = state.filter(attribute => attribute.productAttributeId !== action.newAttribute.productAttributeId);
      return [...replaceStep, action.newAttribute];
    }
    case 'removeAttribute': {
      return state.filter(attribute => attribute.productAttributeId !== action.attributeId);
    }
    default: {
      throw new Error('Unknown StateReducer task');
    }
  }
};

const getValidatedValue = (value: string, options: string[]): string => {
  if (!options.includes(value)) {
    return options[0];
  }

  return value;
};

export const useProductAttributeStates = () => {
  const [attributeStates, setAttributeStates] = useState<ProductAttributeState[]>([]);

  // Use the StateReducer
  const runStateReducer = (action: StateReducerAction) => {
    setAttributeStates(old => {
      return stateReducer(old, action);
    });
  };

  /**
   * Add an Attribute to the ProductAttributeStates
   */
  const addAttribute: AddAttributeFunction = (newAttribute: ProductAttributeState, product: StationeryProduct) => {
    // Create validation wrappers for getters and setters.
    // Forces optionId to conform to allowed optionIDs for product.
    // Safely handles edge case where draft is using an option,
    // and then that option is removed. Without this, we would try and
    // generate a price for that option, would would cause an error in
    // PrintPriceCalculator

    const productAttributes = product.attributes.map(a => a.attributeId);
    if (!productAttributes.includes(newAttribute.productAttributeId)) {
      return;
    }

    const validateGetOptionId = (draftData: ThemeJson) => {
      const optionId = newAttribute.getOptionId(draftData);
      const validatedOptionId = getValidatedValue(optionId, newAttribute.allowedOptionIDs);
      if (optionId !== validatedOptionId) {
        newAttribute.setOptionId(validatedOptionId);
      }

      return validatedOptionId;
    };

    const validateSetOptionId = (newState: string) => {
      const optionId = getValidatedValue(newState, newAttribute.allowedOptionIDs);
      newAttribute.setOptionId(optionId);
    };

    runStateReducer({
      task: 'registerAttribute',
      newAttribute: {
        ...newAttribute,
        getOptionId: validateGetOptionId,
        setOptionId: validateSetOptionId,
      },
    });
  };

  /**
   * Get an Attribute from ProductAttributeStates.
   * @returns Attribute, including getter and setter for it's data.
   */
  const getAttribute = (attributeId: ProductAttributeState['productAttributeId']) => {
    return attributeStates.find(attr => attr.productAttributeId === attributeId);
  };

  const getFullState = (draftData: ThemeJson): { attributeId: string; optionId: string }[] => {
    return attributeStates.map(state => {
      return {
        attributeId: state.productAttributeId,
        optionId: state.getOptionId(draftData),
      };
    });
  };

  const destroyLinks = () => {
    setAttributeStates([]);
  };

  return {
    destroyLinks,
    addAttribute,
    getAttribute,
    getFullState,
  };
};
